import React, { useCallback, useEffect, useState } from 'react'
import { api, formatDate, formatDateTime } from '../../utils/api'
import PostageTaskItem from './PostageTaskItem.jsx'

const LIST_URL = '/api/deals/collections/postage-to-cancel'

/**
 * "Postage to cancel" (Trac #993): cancelled collections with a hand job still
 * open, oldest label first, so no label's refund window is missed. `reloadToken`
 * changes when the board cancels a collection, so the list reads again.
 */
export default function PostageToCancel({ reloadToken, onCountChange }) {
  const [collections, setCollections] = useState(null)
  const [loadError, setLoadError] = useState(null)

  const load = useCallback(async () => {
    try {
      const answer = await api.get(LIST_URL)
      setCollections(answer.collections ?? [])
      setLoadError(null)
    } catch (e) {
      setLoadError(`Could not load the postage to cancel: ${e.message}`)
    }
  }, [])

  useEffect(() => { load() }, [load, reloadToken])
  useEffect(() => { if (collections) onCountChange?.(collections.length) }, [collections, onCountChange])

  function taskDone(updated) {
    setCollections(prev => prev
      .map(collection => ({
        ...collection,
        tasks: collection.tasks.map(t => t.collection_postage_task_id === updated.collection_postage_task_id ? updated : t),
      }))
      .filter(collection => collection.tasks.some(t => !t.done_at)))
  }

  if (loadError) {
    return (
      <div role="alert" className="card" style={{ padding: 'var(--space-4)' }}>
        <p style={{ margin: 0 }}>{loadError}</p>
        <button className="btn btn-secondary" style={{ marginTop: 'var(--space-3)' }} onClick={load}>Try again</button>
      </div>
    )
  }
  if (collections === null) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}><div className="loading-spinner" /></div>
  }
  if (collections.length === 0) {
    return (
      <div className="card" style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
        <p style={{ margin: 0, fontWeight: 600 }}>No postage to cancel</p>
        <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-secondary)' }}>
          When a collection is cancelled and a label needs cancelling by hand, it shows here.
        </p>
      </div>
    )
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Collection</th>
            <th scope="col">Label bought</th>
            <th scope="col">Cancelled</th>
            <th scope="col">To do</th>
          </tr>
        </thead>
        <tbody>
          {collections.map(collection => (
            <CollectionRow key={collection.quote_collection_id} collection={collection} onTaskDone={taskDone} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function CollectionRow({ collection, onTaskDone }) {
  return (
    <tr aria-label={collection.quote_reference_code}>
      <td>
        <div style={{ fontWeight: 600 }}>{collection.quote_reference_code}</div>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          {collection.reference_code}{collection.carrier ? ` · ${collection.carrier}` : ''}
        </div>
      </td>
      <td>{collection.oldest_label_bought_at ? formatDate(collection.oldest_label_bought_at) : 'Unknown'}</td>
      <td>
        <div>{collection.cancelled_at ? formatDateTime(collection.cancelled_at) : ''}</div>
        {collection.cancelled_by && (
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>{collection.cancelled_by}</div>
        )}
      </td>
      <td>
        {collection.tasks.map(task => (
          <PostageTaskItem key={task.collection_postage_task_id} task={task} onDone={onTaskDone} />
        ))}
      </td>
    </tr>
  )
}
